import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ReplaySubject, firstValueFrom } from 'rxjs';
import { DeploymentConfig, Endpoint, fromBoolean } from './models';

@Injectable({
  providedIn: 'root',
})
export class ConfigService {
  private readonly http = inject(HttpClient);

  public readonly configLoaded = new ReplaySubject<void>();
  private deploymentConfig: DeploymentConfig | undefined;

  public async load(): Promise<void> {
    this.deploymentConfig = await firstValueFrom(this.http.get<DeploymentConfig>('/assets/config/deployment.json'), { defaultValue: undefined });
    this.configLoaded.next();
    this.configLoaded.complete();
  }

  public getAwsMarketplaceUrl() {
    return this.deploymentConfig['aws-marketplace'];
  }

  public getDirectoryClientId() {
    return this.deploymentConfig['directory-client-id'];
  }

  public getGoogleClientId() {
    return this.deploymentConfig['google-client-id'];
  }

  public getIdaaswebappUrl() {
    return this.deploymentConfig['idaas-webapp'];
  }

  public getNomidioLoginClientId() {
    return this.deploymentConfig['nomidio-login-client-id'];
  }

  public getCorpInviteRequiresPhone() {
    return fromBoolean(this.deploymentConfig['corp-invite-requires-phone']) ?? false;
  }


  /**
   * Get an endpoint, which is a URL ending with no forward slash.
   * @param endpoint The endpoint to grab
   * @returns The endpoint value
   */
  public getEndpoint(endpoint: Endpoint): string {
    return this.getDeploymentConfigEntry(endpoint);
  }

  /**
   * Get a config entry in a type-unsafe way. Throws errors if the value isn't available.
   * @param entryName The config entry to fetch
   * @returns The config entry, if it's defined
   * @throws If config isn't loaded, or the requested config entry doesn't exist
   */
  private getDeploymentConfigEntry(entryName: string): string {
    if (!this.deploymentConfig) {
      throw Error(`Config value ${entryName} was requested but config isn't loaded`);
    }
    const value = this.deploymentConfig[entryName];
    if (typeof value !== 'undefined') {
      return value;
    } else {
      throw Error(`Config value ${entryName} was requested but doesn't exist`);
    }
  }
}
